// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.
import { z } from 'zod';
import { INVERSIS_COUNTRY_OPTIONS } from './inversis-id-map';
import { validateNifNie } from './personal-data';

export const pepDetailSchema = z
  .object({
    position: z.string().min(1, 'Position is required'),
    institution: z.string().min(1, 'Institution is required'),
    endDate: z.string().optional(),
    hasRelatedAdditionalIncome: z.boolean(),
    additionalIncomeDetail: z.string().optional(),
  })
  .check(({ value, issues }) => {
    if (value.hasRelatedAdditionalIncome) {
      const detail = value.additionalIncomeDetail?.trim();

      if (!detail) {
        issues.push({
          code: 'custom',
          input: value.additionalIncomeDetail,
          path: ['additionalIncomeDetail'],
          message: 'Additional income detail is required',
        });
      }
    }
  });

export const publicPersonRelationshipDetailSchema = z.object({
  personName: z.string().min(1, 'Person name is required'),
  relationship: z.string().min(1, 'Relationship is required'),
  publicPosition: z.string().min(1, 'Public position is required'),
  institution: z.string().min(1, 'Institution is required'),
});

export const beneficialOwnerDetailSchema = z
  .object({
    firstName: z.string().min(1, 'First name is required'),
    lastName: z.string().min(1, 'Last name is required'),
    documentNumber: z.string().min(1, 'Document number is required'),
  })
  .check(({ value, issues }) => {
    const data = value;

    const isInvalidDocumentNumber = !validateNifNie(data.documentNumber);

    if (isInvalidDocumentNumber) {
      issues.push({
        code: 'custom',
        input: data.documentNumber,
        path: ['documentNumber'],
        message: 'Invalid NIF/NIE format',
      });
    }
  });

export const legalDeclarationsSchema = z
  .object({
    isPoliticallyExposedPerson: z.boolean(),
    pepDetail: pepDetailSchema.optional(),
    isRelatedToPublicPerson: z.boolean(),
    publicPersonRelationshipDetail: publicPersonRelationshipDetailSchema.optional(),
    isUsTaxResidence: z.boolean(),
    taxResidenceCountry: z.string().optional(),
    taxResidenceDocumentNumber: z.string().optional(),
    taxResidenceDocumentNumberEdited: z.boolean().optional(),
    investmentOwnership: z.enum(['self', 'other-person']),
    beneficialOwnerDetail: beneficialOwnerDetailSchema.optional(),
  })
  .check(({ value, issues }) => {
    const data = value;

    if (data.isPoliticallyExposedPerson && typeof data.pepDetail === 'undefined') {
      issues.push({
        code: 'custom',
        input: data.pepDetail,
        path: ['pepDetail'],
        message: 'PEP detail is required',
      });
    }

    if (
      data.isRelatedToPublicPerson &&
      typeof data.publicPersonRelationshipDetail === 'undefined'
    ) {
      issues.push({
        code: 'custom',
        input: data.publicPersonRelationshipDetail,
        path: ['publicPersonRelationshipDetail'],
        message: 'Public person relationship detail is required',
      });
    }

    if (data.isUsTaxResidence && !data.taxResidenceDocumentNumber?.trim()) {
      issues.push({
        code: 'custom',
        input: data.taxResidenceDocumentNumber,
        path: ['taxResidenceDocumentNumber'],
        message: 'US tax residence document number is required',
      });
    }

    if (!data.isUsTaxResidence && !data.taxResidenceCountry) {
      issues.push({
        code: 'custom',
        input: data.taxResidenceCountry,
        path: ['taxResidenceCountry'],
        message: 'Tax residence country is required',
      });
    }

    if (!data.isUsTaxResidence && !data.taxResidenceDocumentNumber?.trim()) {
      issues.push({
        code: 'custom',
        input: data.taxResidenceDocumentNumber,
        path: ['taxResidenceDocumentNumber'],
        message: 'Tax residence document number is required',
      });
    }

    if (
      data.investmentOwnership === 'other-person' &&
      typeof data.beneficialOwnerDetail === 'undefined'
    ) {
      issues.push({
        code: 'custom',
        input: data.beneficialOwnerDetail,
        path: ['beneficialOwnerDetail'],
        message: 'Beneficial owner detail is required',
      });
    }

    if (
      data.taxResidenceCountry &&
      !INVERSIS_COUNTRY_OPTIONS.has(data.taxResidenceCountry)
    ) {
      issues.push({
        code: 'custom',
        input: data.taxResidenceCountry,
        path: ['taxResidenceCountry'],
        message: 'Invalid country ID',
      });
    }
  });

export type PepDetailFormData = z.infer<typeof pepDetailSchema>;

export type PublicPersonRelationshipDetailFormData = z.infer<
  typeof publicPersonRelationshipDetailSchema
>;

export type BeneficialOwnerDetailFormData = z.infer<
  typeof beneficialOwnerDetailSchema
>;

export type LegalDeclarationsFormData = z.infer<typeof legalDeclarationsSchema>;
